import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import { bubEvents } from 'javascripts/shared/events/bub_events';
import { graphQL } from 'javascripts/shared/graphql';
import { graphQLMutations } from 'javascripts/shared/graphql/mutations';
import { graphQLQueries } from 'javascripts/shared/graphql/queries';
import { useGraphQLClientData } from 'javascripts/hooks/use-graphql-client-data';
import { usePaginatedGraphQL } from 'javascripts/hooks/use-paginated-graphql';
import { userData } from 'javascripts/shared/utils/user_data';

import { BookCardCollectionCondensed } from 'javascripts/components/book_card_collection_condensed';
import { Loader } from 'javascripts/components/loader';
import { LoadMoreButton } from 'javascripts/components/search/load_more_button';
import { PersonFollowButton } from 'javascripts/components/person_follow_button';
import { PersonFollowEducationModal } from 'javascripts/components/person_follow_education_modal';
import { SortAndFilterBar } from 'javascripts/components/sort_and_filter_bar';
import { ValuePropSection } from 'javascripts/components/value_prop_section';

import styles from 'stylesheets/apps/person_app';

const PERSON_FOLLOW_EDUCATION_MODAL = 'AUTHOR_FOLLOW_EDUCATION_MODAL';
const SORT_OPTIONS = [
  { label: 'Default', value: 'default' },
  { label: 'Popularity', value: 'popular' },
  { label: 'Recency', value: 'recency' },
  { label: 'Price (Low to High)', value: 'price_asc' },
  { label: 'Price (High to Low)', value: 'price_desc' },
];

export function PersonApp({ showAuthorByDefault, person }) {
  const [authorToggleSelected, setAuthorToggleSelected] = useState(showAuthorByDefault);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentSort, setCurrentSort] = useState('default');
  const [errorMessage, setErrorMessage] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [showFollowEducationModal, setShowFollowEducationModal] = useState(false);
  const showFollowButton = userData.isLoggedIn();

  const { currentUserNotificationView = null, forceRefreshData: refreshNotificationView } = useGraphQLClientData(
    graphQLQueries.userNotificationView,
    { notification: PERSON_FOLLOW_EDUCATION_MODAL },
  );

  const {
    loading,
    canFetchMore,
    objects: audiobooks,
    totalCount,
  } = usePaginatedGraphQL({
    query: authorToggleSelected ? graphQLQueries.authorAudiobooks : graphQLQueries.narratorPaginatedAudiobooks,
    data: { slug: person.slug, sort: currentSort, hideUnpromotable: false },
    currentPage,
    queryName: authorToggleSelected ? 'author.paginatedAudiobooks' : 'narrator.paginatedAudiobooks',
  });

  useEffect(() => {
    if (!loading) {
      setHasFetched(true);
    }
  }, [loading]);

  const trackingCategory = authorToggleSelected ? 'Author' : 'Narrator';

  function toggleSelectedPage() {
    bubEvents.track('Person Page', 'Clicked tab', {
      label: 'Tab name',
      value: authorToggleSelected ? 'Books narrated' : 'Books authored',
    });

    setHasFetched(false);
    setCurrentPage(1);
    setAuthorToggleSelected(!authorToggleSelected);
  }

  function renderToggle() {
    if (!person.isAuthor || !person.isNarrator) {
      return null;
    }

    return (
      <div className={styles.typeToggleSection}>
        <button className={styles.toggle} disabled={authorToggleSelected} onClick={toggleSelectedPage}>
          Books written
        </button>
        <button className={styles.toggle} disabled={!authorToggleSelected} onClick={toggleSelectedPage}>
          Books narrated
        </button>
      </div>
    );
  }

  function onFollowButtonError(action) {
    setErrorMessage(`An error occurred while ${action} ${person.name}. Please refresh the page and try again.`);
  }

  function onFollowChange({ following }) {
    if (_.isNil(currentUserNotificationView) && following) {
      setShowFollowEducationModal(true);
    }
    setErrorMessage(null);
  }

  function renderHeader() {
    return (
      <div className={styles.header}>
        <div>
          <div className={styles.typeInfo}>{person.typeDescription}</div>
          <div className={styles.nameSection}>
            <div className={styles.name}>{person.name}</div>
            <div className={styles.followButton}>
              {showFollowButton ? (
                <PersonFollowButton
                  onError={onFollowButtonError}
                  onFollowChange={onFollowChange}
                  person={person}
                  trackingCategory={`${trackingCategory} page`}
                />
              ) : null}
            </div>
          </div>
          {renderToggle()}
        </div>
      </div>
    );
  }

  function onSortSelect(sort) {
    bubEvents.track('Sort audiobooks', 'Clicked from dropdown', {
      label: 'Sort option',
      value: sort,
    });

    setCurrentSort(sort);
    setCurrentPage(1);
  }

  function renderSortBar() {
    return (
      <SortAndFilterBar
        currentSort={currentSort}
        onSortSelect={onSortSelect}
        showFilterOption={false}
        showFormatOption={false}
        showSearchOption={false}
        showSelectOption={false}
        showingCount={audiobooks.length}
        sortOptions={SORT_OPTIONS}
        totalCount={totalCount}
      />
    );
  }

  function renderAudiobooks() {
    if (hasFetched && !loading && audiobooks.length === 0) {
      return (
        <div className={styles.emptyMessage}>
          {"Sorry, we don't currently have any audiobooks "}
          {`${authorToggleSelected ? 'authored' : 'narrated'} by ${person.name} available`}
        </div>
      );
    }

    return (
      <div className={styles.audiobookSection}>
        <BookCardCollectionCondensed
          animate={currentPage === 1}
          audiobooks={audiobooks}
          loading={loading && !hasFetched}
          showCoverButtons
          showStarRatings
          trackingCategory={`Browse ${trackingCategory} Books`}
        />
      </div>
    );
  }

  function handleLoadMoreClick() {
    bubEvents.track(`Browse ${trackingCategory} Books`, 'Clicked load more', {
      label: 'requested page',
      value: currentPage + 1,
    });

    setCurrentPage(currentPage + 1);
  }

  function renderLoadMore() {
    if (loading) {
      return <Loader />;
    }

    if (canFetchMore) {
      return <LoadMoreButton loadMore={handleLoadMoreClick} />;
    }

    return null;
  }

  function onCloseFollowEducationModal() {
    setShowFollowEducationModal(false);
    graphQL
      .get(graphQLMutations.createUserNotificationView, { notification: PERSON_FOLLOW_EDUCATION_MODAL })
      .then(() => {
        refreshNotificationView();
      });
  }

  function renderEducationalModal() {
    if (!userData.isLoggedIn()) {
      return false;
    }

    return (
      <PersonFollowEducationModal
        isOpen={showFollowEducationModal}
        onRequestClose={onCloseFollowEducationModal}
        personName={person.name}
      />
    );
  }

  function renderValueProps() {
    if (userData.isLoggedIn()) {
      return null;
    }

    return (
      <div className={styles.valueProps}>
        <ValuePropSection />
      </div>
    );
  }

  return (
    <div className={styles.personApp}>
      {renderHeader()}
      <div className={styles.errorMessage}>{errorMessage}</div>
      {renderSortBar()}
      {renderAudiobooks()}
      {renderLoadMore()}
      {renderEducationalModal()}
      {renderValueProps()}
    </div>
  );
}

PersonApp.propTypes = {
  person: PropTypes.shape({
    followedByCurrentUser: PropTypes.bool.isRequired,
    id: PropTypes.string.isRequired,
    isAuthor: PropTypes.bool.isRequired,
    isNarrator: PropTypes.bool.isRequired,
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    typeDescription: PropTypes.string.isRequired,
  }),
  showAuthorByDefault: PropTypes.bool.isRequired,
};
